import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';

import { applyVerticalStyles, interpolateString, get } from 'ctm-util-functions';
import { badgeUIComponent } from 'ctm-css-themes';

import { pageNameSelector, splitTrafficIdSelector } from 'ctm-selectors';
import { connect } from 'react-redux';
import { getCheckboxDisabledProp, getRadioComponentStateSuffix } from '../utils';
import * as actions from '../actions';

import { Picture } from '../../picture/picture';
import { Icon } from '../../icon/icon';
import { Radio } from '../radio/radio';
import { Tooltip } from '../../tooltip/tooltip';
// eslint-disable-next-line import/no-cycle
import { Checkbox } from '../checkbox/checkbox';
import { DetailedLabel } from '../detailed_label/detailed_label';

import styles from './badge.css';

// @todo this component is getting too big. Suggest splitting it out to a Tile and a circular badge,
// with the styles for different layouts (eg. xs_100_sm_50) potentially being trait sets or part of the UI component.
// It would have many of the same functions as well, similarly with other inputs, so look at making either a higher order component or util functions.
class _Badge extends Component {

    /**
     * Default the focused state to not focused.
     * @param {Object} props The component properties
     */
    constructor(props) {
        super(props);
        this.state = {
            focus: false
        };

        this.styles = applyVerticalStyles(styles, 'badge');
        this.badgeUIComponent = applyVerticalStyles(badgeUIComponent, 'badge_ui');
    }

    /**
     * Convenience function to see if this Badge should be checked based on it's value
     * @returns {Boolean} - True if the element should be checked.
     */
    isChecked = () => {
        const {
            optionProps,
            selectedValues,
            schema,
            value
        } = this.props;
        const elementProps = optionProps || schema.field;
        const elementValue = interpolateString(elementProps.value)(selectedValues);
        return elementProps.isChecked || (value === elementValue);
    };

    /**
     * On updates, check if this input is disabled, if it is, dispatch an action to ensure that it's value is not
     * set on state for the input's identifier.
     */
    componentDidUpdate = () => {
        const {
            optionProps,
            selectedValues,
            schema,
            dispatch
        } = this.props;
        if (getCheckboxDisabledProp(this.props).disabled === true) {
            const elementProps = optionProps || schema.field;
            dispatch(
                actions.resetValueIfSet(
                    schema.identifier,
                    interpolateString(elementProps.value)(selectedValues),
                    schema.stateKey
                )
            );
        }
    };

    /**
     * Activate the tooltip for the current badge.
     */
    activateTooltip = () => {
        const { activateTooltip } = this.props;
        if (activateTooltip) {
            activateTooltip(this.badgeContainer);
        }
    };

    /**
     * Handle user clicking the badge.
     */
    onClickFunction = () => {
        this.activateTooltip();
    };

    /**
     * Handle user hovering over the badge.
     * @param {Event} event The event object
     */
    onMouseHoverFunction = (event) => {
        event.persist();
        this.activateTooltip();
    };

    /**
     * Handle user clicking outside the badge.
     */
    onClickOutside = () => {
        const { deactivateTooltip } = this.props;
        if (deactivateTooltip) {
            deactivateTooltip();
        }
    };

    /**
     * Trigger any onClick functions passed in
     * @param {Object} event The event object
     */
    onClick = (event) => {
        const { value } = this.props;
        const onClickFunction = get(this.props, 'eventProps.onClick');
        if (typeof onClickFunction === 'function') {
            onClickFunction(event);
        }
        // NOTE: This is required because badges and radio button groups can be selected
        // to trigger the next step to load. If you go forward, then back a step, and select
        // the same value, prior to React DOM 15.6.0, it triggered the onChange event.
        // Now we're on React 16, it doesn't trigger subsequent onChange events, so we had to hack this in.
        // @WARN I don't know what this will do if multiple actions are dispatched on the onChange!!!!
        // But it should just be what it was before we upgraded anyway.
        if (value === event.target.value) {
            this.onChange(event);
        }
    };

    /**
     * Call the setValue function when the value of the input has been changed
     * by the user.
     * @param {Object} event - event object
     */
    onChange = (event) => {
        const { onChange, dispatch } = this.props;
        event.persist();
        if (onChange) {
            // For filters etc where we create the badge from a component
            onChange(event);
            return;
        }

        const { schema = {}, optionProps = [] } = this.props;
        // if checked, set the value, else, clear the value
        let value = '';
        if (event.target.checked) {
            value = event.target.value;
        }

        dispatch(
            actions.setFormValue(
                schema.backendIdentifier || schema.identifier,
                value,
                schema.stateKey,
                true
            )
        );

        let onClickActions = [];
        if (optionProps.onClickActions) {
            onClickActions = onClickActions.concat(optionProps.onClickActions);
        }
        if (schema.onClickActions) {
            onClickActions = onClickActions.concat(schema.onClickActions);
        }

        if (onClickActions.length) {
            onClickActions.forEach((action) => {
                // eslint-disable-next-line no-param-reassign
                action.stateKey = schema.stateKey;
                // spread action after value so if action has specific value defined in schema, use that value
                dispatch({ value, ...action });
            });
        }
    };

    /**
     * We need to apply focused states to container divs, can't use :focus pseudo selector,
     * so we need to re-render the component.
     * @param {Boolean} state True if focusing, False is blurring.
     */
    toggleFocus = (state) => {
        this.setState({
            focus: state
        });
    };

    /**
     * Get the content for the badge - either an icon or a string
     * @param  {Object} elementProps - The properties for the option from the schema
     * @param  {String} elementId - The elements unique ID.
     * @returns {Array} An icon or simple text.
     */
    getContent = (elementProps, elementId) => {
        const { selectedValues } = this.props;
        const { icon, innerLabel, detailedLabel, content, description } = elementProps;
        const children = [];
        if (icon) {
            children.push(<Icon key={`${elementId}.icon`} variant={icon} />);
        } else if (innerLabel) {
            children.push(
                <span key={`${elementId}.innerLabel`}>
                    {interpolateString(innerLabel)(selectedValues)}
                    {description
                        && (
                            <span className={styles.option_description}>
                                {interpolateString(description)(selectedValues)}
                            </span>
                        )}
                </span>
            );
        } else if (detailedLabel) {
            children.push(
                <DetailedLabel
                    key={`${elementId}.detailedLabel`}
                    content={content}
                    detailedLabel={detailedLabel}
                    selectedValues={selectedValues}
                />
            );
        }

        return children;
    };

    /**
     * Determine whether we are rendering a checkbox or a radio component below the badge.
     * NOTE: This does not get rendered if there is a innerLabel property.
     * @param {String} inputType The input type - checkbox or radio
     * @returns {Node} The correct element to render
     */
    getFormControl = (inputType) => {
        if (inputType === 'radio') {
            return (
                <Radio
                    {...this.props}
                    onClickFunction={this.onClickFunction}
                    controlPositionClassName={this.styles.control_top_left_of_badge}
                />
            );
        }
        if (inputType === 'checkbox') {
            const { checkboxSchema, schema } = this.props;
            return (
                <Checkbox {...this.props} schema={checkboxSchema || schema} controlPositionClassName={this.styles.control_top_left_of_badge} />);
        }
        return null;
    };

    /**
     * Check visibility rules to see if there is one for this badge, if so return the visibility status
     * @returns {Boolean} - True if this badge should be visible
     */
    isVisible = () => {
        // todo: this does not work for the checkboxes in energy preferences section as they dont have optionProps
        const rule = get(this.props, `rules.visibility_${get(this.props, 'optionProps.value')}`);
        return rule ? rule.result : true;
    };

    /**
     * Renders the badge logo if imagePath is provided
     * @param {String} imagePath - image path
     * @param {String} value - the value of the brand logo to display
     * @returns {Node|null} - Returns the logo component for the badge
     */
    getInnerImage = (imagePath, value) => {
        if (!imagePath) return null;
        const path = `/evt/assets/images/logos/${imagePath}/${value}.png`;
        const elementProps = { image: { src: path, alt: 'logo' } };
        return <Picture className={styles.brand_logo} elementProps={elementProps} />;
    };

    getOptionImage = (imagePath, value) => {
        if (!imagePath) return null;
        const path = `/evt/assets/images/forms/${imagePath}/${value}.png`;
        const elementProps = { image: { src: path, alt: 'image' } };
        return <Picture className={styles.option_image} elementProps={elementProps} />;
    };

    /**
     * Renders the colour icon in the badge
     * @param {Boolean} shouldDisplay if it should display the colour
     * @param {String} value - the value of the colour to display
     * @returns {Node|null} - Returns the colour icon for the badge
     */
    getColour = (shouldDisplay, value = '') => {
        if (!shouldDisplay) return null;
        const className = classNames(this.styles[value.toLowerCase()], this.styles.colour_base);
        return <div className={className} />;
    };

    /**
     * Conceptually at a field schema level, a group of radio buttons is one "field",
     * as they all share the same input name as you can only select one value.
     * Each of these radio buttons in the group requires a
     * Even a group of checkboxes would have different names.
     * @returns {Node} A rendered badge component in a container.
     */
    render = () => {
        if (!this.isVisible()) {
            return null;
        }

        const { focus } = this.state;
        const { schema, optionProps, tooltipTrigger, selectedValues, experimentIdFromState, pageName } = this.props;
        const logoPath = schema.field.logoPath;
        const imagePath = schema.field.imagePath;
        const displayColourIcon = schema.field.colourIcon;
        const badgeStyles = this.styles;

        // It is optionProps if its a radio or schema.field if its a single checkbox.
        // Radios will always have schema.field.
        const elementProps = optionProps || schema.field;
        const inputType = schema.field.metaType || 'radio';
        const elementValue = interpolateString(elementProps.value)(selectedValues);
        let elementId = `${schema.identifier}.${elementValue}`;
        if (inputType === 'checkbox') {
            elementId = `${schema.identifier}.${schema.field.value}`;
        }

        // Use correct UI Component and state.
        const UIComponentPrefix = schema.field.UIComponent;
        const stateSuffix = getRadioComponentStateSuffix(this.props);
        const UIComponent = `${UIComponentPrefix}${stateSuffix}`;
        const containerState = `container_${UIComponentPrefix}${stateSuffix}`;
        const focusClass = focus ? 'state_set_focused' : null;
        const colourClass = elementProps.colour ? elementProps.colour : null;
        let componentClass = classNames(
            this.badgeUIComponent[UIComponent],
            this.badgeUIComponent[focusClass],
            badgeStyles[colourClass]
        );
        const circleBadge = elementProps.icon || elementProps.colour;

        const containerClassNames = classNames(
            this.badgeUIComponent[containerState],
            badgeStyles.root,
            badgeStyles[`variant_${elementProps.variant}`],
            circleBadge ? null : componentClass // if no icon or color, it is a tile ui component, we then want to apply the style to the container
        );

        // avoid applying any styles to inner div for the tile ui component
        componentClass = circleBadge ? componentClass : styles.inner_div;

        // UI component's inner content container for text tiles
        let componentInnerContainerClass = circleBadge ? null : get(badgeStyles,
            `variant_${schema.field.labelVariant}`,
            badgeStyles.variant_inner_label_container_small);

        if (experimentIdFromState === 'ExpTest3' && (pageName === 'car_year' || pageName === 'car_model')) {
            componentInnerContainerClass = get(badgeStyles, 'variant_inner_label_container_small_align_center',
                badgeStyles.variant_inner_label_container_small);
        }

        // UI component's inner content
        const componentSuffix = circleBadge ? 'inner_icon' : 'inner_label';
        const componentInnerClass = get(badgeStyles, `badge_${componentSuffix}`, null);

        let element;
        // The idea is the if we have text in the label, we shouldn't need a radio button and text below.
        // If this idea changes, we'd have to change things.
        if (!elementProps.innerLabel && !elementProps.detailedLabel) {
            element = this.getFormControl(inputType);
        } else {
            // If its just got an innerLabel, we need to add the input as well.
            element = (
                <input
                    onClick={this.onClick}
                    onChange={this.onChange}
                    checked={this.isChecked()}
                    {...getCheckboxDisabledProp(this.props)}
                    type={inputType}
                    name={schema.identifier}
                    value={elementValue}
                    id={elementId}
                    data-id={elementId}
                />
            );
        }

        return (
            <div className={containerClassNames}>
                {
                    elementProps.tooltip && tooltipTrigger
                    && (
                        <Tooltip
                            onClickOutside={this.onClickOutside}
                            placement="top"
                            variant="grey"
                            trigger={tooltipTrigger}>
                            {elementProps.tooltip}
                        </Tooltip>
                    )
                }
                <label
                    className={componentInnerContainerClass}
                    htmlFor={elementId}
                    onFocus={() => this.toggleFocus(true)}
                    onBlur={() => this.toggleFocus(false)}
                    style={ imagePath ? { display: 'block' } : {} }
                >
                    {this.getOptionImage(imagePath, elementProps.value)}
                    <div
                        ref={(label) => {
                            this.badgeContainer = label;
                        }}
                        id={elementValue}
                        className={componentClass}
                        onMouseOver={this.onMouseHoverFunction}
                        onTouchStart={this.onMouseHoverFunction}
                        style={ imagePath ? { display: 'inline' } : { display: 'flex' } }
                    >
                        {this.getColour(displayColourIcon, elementProps.value)}
                        {this.getInnerImage(logoPath, elementProps.value)}
                        <span className={componentInnerClass}>
                            {this.getContent(elementProps, elementId)}
                        </span>
                    </div>
                    {element}
                </label>
            </div>
        );
    };

}

_Badge.propTypes = {
    activateTooltip: PropTypes.func,
    deactivateTooltip: PropTypes.func,
    tooltipTrigger: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object
    ]),
    schema: PropTypes.shape({
        field: PropTypes.object,
        identifier: PropTypes.string,
        backendIdentifier: PropTypes.string,
        onClickActions: PropTypes.array,
        stateKey: PropTypes.string
    }).isRequired, // The schema for this form element
    checkboxSchema: PropTypes.object,
    optionProps: PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string,
        icon: PropTypes.string,
        colour: PropTypes.string,
        innerLabel: PropTypes.string,
        content: PropTypes.object,
        detailedLabel: PropTypes.string,
        onClickActions: PropTypes.array
    }),
    value: PropTypes.string.isRequired,
    // If we want to change the component's validation styles, we can use touched and validation rules
    // to apply a different class.
    // touched: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    selectedValues: PropTypes.object,
    experimentIdFromState: PropTypes.string,
    pageName: PropTypes.string
};

const mapStateToProps = (state) => {
    const experimentIdFromState = splitTrafficIdSelector(state);
    const pageName = pageNameSelector(state);
    return {
        experimentIdFromState,
        pageName
    };
};

const Badge = connect(mapStateToProps)(_Badge);

export { _Badge, Badge };
// export { Badge };
